import { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Search, UserPlus, MessageSquare } from "lucide-react";
import { API, AuthContext } from "../App";
import Avatar from "./Avatar";

const UserSearchModal = ({ onClose, onMessage }) => {
  const { token, user } = useContext(AuthContext);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState({});
  const debounceRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(q.trim())}`, { headers });
        setResults((res.data || []).filter((u) => u.user_id !== user?.user_id));
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const addFriend = async (u) => {
    try {
      await axios.post(`${API}/friends/request`, { friend_user_id: u.user_id }, { headers });
      setSent((s) => ({ ...s, [u.user_id]: true }));
      toast.success(`Friend request sent to ${u.username}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send friend request");
    }
  };

  return (
    <div className="fixed inset-0 z-[9750] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="user-search-modal">
      <div className="retro-window w-96 max-w-full" onClick={(e) => e.stopPropagation()}>
        <div className="retro-title-bar">
          <span className="flex items-center gap-1"><Search className="w-3 h-3" /> Find Friends</span>
          <button className="retro-button px-2 py-0 text-xs" onClick={onClose} data-testid="user-search-close"><X className="w-3 h-3" /></button>
        </div>
        <div className="p-3 bg-[#C0C0C0]">
          <div className="flex items-center gap-1 bg-white border-2 border-[#808080] px-2 py-1 mb-2">
            <Search className="w-4 h-4 text-gray-500" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by username…"
              className="flex-1 outline-none text-sm font-arial"
              data-testid="user-search-input"
            />
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1" data-testid="user-search-results">
            {loading && <p className="text-xs text-center text-[#555] py-3">Searching…</p>}
            {!loading && q.trim() && results.length === 0 && (
              <p className="text-xs text-center text-[#555] py-3">No users found for "{q}"</p>
            )}
            {!loading && !q.trim() && (
              <p className="text-xs text-center text-[#555] py-3">Type a username to find friends.</p>
            )}
            {results.map((u) => (
              <div key={u.user_id} className="flex items-center gap-2 bg-white border border-[#808080] px-2 py-1.5" data-testid={`search-result-${u.user_id}`}>
                <Avatar avatar={u.avatar} username={u.username} color={u.avatar_color || "#7B0099"} size={26} />
                <span className="flex-1 font-arial text-sm truncate">{u.username}</span>
                {onMessage && (
                  <button className="retro-button px-2 py-0.5 text-xs" onClick={() => onMessage(u)} data-testid={`search-msg-${u.user_id}`} title="Message">
                    <MessageSquare className="w-3 h-3" />
                  </button>
                )}
                <button
                  className="retro-button px-2 py-0.5 text-xs flex items-center gap-1 bg-[#7B0099] text-white border-[#7B0099]"
                  onClick={() => addFriend(u)}
                  disabled={sent[u.user_id]}
                  data-testid={`search-addfriend-${u.user_id}`}
                >
                  <UserPlus className="w-3 h-3" /> {sent[u.user_id] ? "Sent" : "Add"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSearchModal;
